import { cn } from '../../lib/utils';
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  MessageSquare,
  Upload,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

export interface KycTimelineEvent {
  id: string;
  eventType: string;
  kycType: string;
  actor: string;
  actorName: string;
  details?: Record<string, any>;
  timestamp: string;
}

interface KycTimelineProps {
  events: KycTimelineEvent[];
  className?: string;
}

const eventConfig: Record<string, {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}> = {
  SUBMITTED: {
    label: 'Documentos Enviados',
    icon: Upload,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  IN_REVIEW: {
    label: 'Em Análise',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
  },
  DOCUMENT_ADDED: {
    label: 'Documento Adicionado',
    icon: FileText,
    color: 'text-primary-400',
    bgColor: 'bg-primary-500/20',
  },
  DOCUMENT_VIEWED: {
    label: 'Documento Visualizado',
    icon: Eye,
    color: 'text-surface-400',
    bgColor: 'bg-surface-500/20',
  },
  APPROVED: {
    label: 'Aprovado',
    icon: CheckCircle2,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  REJECTED: {
    label: 'Rejeitado',
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  RESUBMITTED: {
    label: 'Reenviado',
    icon: RefreshCw,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
  },
  ADDITIONAL_DOCS_REQUESTED: {
    label: 'Documentos Solicitados',
    icon: AlertTriangle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
  },
  ANNOTATION_ADDED: {
    label: 'Anotação Adicionada',
    icon: MessageSquare,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
};

const kycTypeLabels: Record<string, string> = {
  ORIGIN: 'Origem',
  DESTINATION: 'Destino',
};

export function KycTimeline({ events, className }: KycTimelineProps) {
  if (!events || events.length === 0) {
    return (
      <div className={cn("text-center py-8 text-surface-500", className)}>
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Nenhum evento registrado</p>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      {/* Linha vertical conectando eventos */}
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-surface-700" />

      <div className="space-y-4">
        {events.map((event, index) => {
          const config = eventConfig[event.eventType] || {
            label: event.eventType,
            icon: FileText,
            color: 'text-surface-400',
            bgColor: 'bg-surface-500/20',
          };
          const Icon = config.icon;

          // Determinar se é ação de admin ou usuário
          const isAdmin = event.actor.startsWith('ADMIN:');
          const actorLabel = isAdmin ? `Admin: ${event.actorName}` : event.actorName;

          return (
            <div key={event.id} className="relative flex gap-4">
              {/* Ícone do evento */}
              <div className={cn(
                "relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                config.bgColor
              )}>
                <Icon className={cn("w-5 h-5", config.color)} />
              </div>

              {/* Conteúdo do evento */}
              <div className="flex-1 pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={cn("font-medium", config.color)}>
                      {config.label}
                    </p>
                    <p className="text-sm text-surface-500">
                      {actorLabel}
                      {event.kycType && (
                        <span className="ml-2 px-1.5 py-0.5 bg-surface-800 rounded text-xs">
                          KYC {kycTypeLabels[event.kycType] || event.kycType}
                        </span>
                      )}
                    </p>
                  </div>
                  <time className="text-xs text-surface-500 whitespace-nowrap">
                    {formatTimestamp(event.timestamp)}
                  </time>
                </div>

                {/* Detalhes do evento */}
                {event.details && Object.keys(event.details).length > 0 && (
                  <div className="mt-2 p-2 bg-surface-800/50 rounded-lg text-sm">
                    {renderEventDetails(event.eventType, event.details)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `há ${diffMinutes} min`;
  }

  if (diffHours < 24) {
    return `há ${diffHours}h`;
  }

  if (diffDays < 7) {
    return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function renderEventDetails(eventType: string, details: Record<string, any>): React.ReactNode {
  switch (eventType) {
    case 'SUBMITTED':
    case 'RESUBMITTED':
      return (
        <span className="text-surface-400">
          {details.documentsCount} documento{details.documentsCount !== 1 ? 's' : ''} enviado{details.documentsCount !== 1 ? 's' : ''}
        </span>
      );

    case 'DOCUMENT_ADDED':
      return (
        <span className="text-surface-400">
          {details.documentTypeName}
          {details.uploadedByAdmin && (
            <span className="ml-2 text-xs text-orange-400">(enviado pelo admin)</span>
          )}
        </span>
      );

    case 'DOCUMENT_VIEWED':
      return (
        <span className="text-surface-400">
          {details.documentTypeName}
        </span>
      );

    case 'REJECTED':
      return (
        <span className="text-red-400">
          Motivo: {details.reason}
        </span>
      );

    case 'APPROVED':
      return details.notes ? (
        <span className="text-surface-400">
          Observação: {details.notes}
        </span>
      ) : null;

    case 'ADDITIONAL_DOCS_REQUESTED':
      return (
        <div className="space-y-1">
          <span className="text-surface-400">{details.reason}</span>
          {details.requiredDocuments?.length > 0 && (
            <ul className="list-disc list-inside text-surface-500 text-xs">
              {details.requiredDocuments.map((doc: string, i: number) => (
                <li key={i}>{doc}</li>
              ))}
            </ul>
          )}
        </div>
      );

    case 'ANNOTATION_ADDED':
      return (
        <span className="text-surface-400">
          Em: {details.documentTypeName}
        </span>
      );

    default:
      return (
        <pre className="text-xs text-surface-500 overflow-auto">
          {JSON.stringify(details, null, 2)}
        </pre>
      );
  }
}

export default KycTimeline;
